import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/layout/Header';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Users, Package, FileText, DollarSign, TrendingUp, 
  CheckCircle, XCircle, Eye, Building, Shield
} from 'lucide-react';
import { adminApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersPage, setUsersPage] = useState(1);
  const [totalUsersPages, setTotalUsersPages] = useState(1);

  useEffect(() => {
    fetchData();
  }, [usersPage]);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes] = await Promise.all([
        adminApi.getStats(),
        adminApi.getUsers({ page: usersPage, limit: 20 })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users || []);
      setTotalUsersPages(usersRes.data.pages || 1);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyUser = async (userId) => {
    try {
      await adminApi.verifyUser(userId);
      toast.success('User verified successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to verify user');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="container-main py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-slate-200 rounded w-1/4"></div>
            <div className="grid grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-24 bg-slate-200 rounded-xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      <div className="container-main py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-1">Admin Dashboard</h1>
          <p className="text-slate-600">Manage users, products, and platform activity</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-[#2563EB]" />
              <span className="text-xs text-slate-500">Total Users</span>
            </div>
            <p className="text-xl font-bold text-slate-900">{stats?.users || 0}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Building className="w-4 h-4 text-[#2563EB]" />
              <span className="text-xs text-slate-500">Suppliers</span>
            </div>
            <p className="text-xl font-bold text-slate-900">{stats?.suppliers || 0}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-[#2563EB]" />
              <span className="text-xs text-slate-500">Buyers</span>
            </div>
            <p className="text-xl font-bold text-slate-900">{stats?.buyers || 0}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-4 h-4 text-[#2563EB]" />
              <span className="text-xs text-slate-500">Products</span>
            </div>
            <p className="text-xl font-bold text-slate-900">{stats?.products || 0}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-[#2563EB]" />
              <span className="text-xs text-slate-500">Active RFQs</span>
            </div>
            <p className="text-xl font-bold text-slate-900">{stats?.rfqs || 0}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-4 h-4 text-[#16A34A]" />
              <span className="text-xs text-slate-500">Revenue</span>
            </div>
            <p className="text-xl font-bold text-[#16A34A]">${stats?.revenue?.toFixed(0) || 0}</p>
          </div>
        </div>

        {/* Users Management */}
        <Tabs defaultValue="users">
          <TabsList className="bg-white border mb-6">
            <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
            <TabsTrigger value="suppliers" data-testid="admin-tab-suppliers">Suppliers</TabsTrigger>
            <TabsTrigger value="buyers" data-testid="admin-tab-buyers">Buyers</TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold text-slate-900">User</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-900">Type</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-900">Membership</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-900">Status</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-900">Joined</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-900">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-slate-900">{u.name}</p>
                          <p className="text-sm text-slate-500">{u.email}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="outline" className="capitalize">{u.user_type}</Badge>
                      </td>
                      <td className="py-3 px-4">
                        <Badge className={
                          u.membership === 'vip' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                          u.membership === 'gold' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                          u.membership === 'silver' ? 'bg-slate-100 text-slate-700 border-slate-200' :
                          'bg-slate-50 text-slate-500 border-slate-200'
                        }>
                          {u.membership}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        {u.is_verified ? (
                          <Badge className="bg-green-50 text-green-700 border-green-200">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Verified
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-50 text-slate-500 border-slate-200">
                            Pending
                          </Badge>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-500">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          {u.user_type === 'supplier' && !u.is_verified && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleVerifyUser(u.user_id)}
                            >
                              <Shield className="w-3 h-3 mr-1" />
                              Verify
                            </Button>
                          )}
                          {u.user_type === 'supplier' && (
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => navigate(`/suppliers/${u.user_id}`)}
                            >
                              <Eye className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {totalUsersPages > 1 && (
                <div className="p-4 border-t flex justify-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={usersPage === 1}
                    onClick={() => setUsersPage(usersPage - 1)}
                  >
                    Previous
                  </Button>
                  <span className="flex items-center px-4 text-sm text-slate-600">
                    Page {usersPage} of {totalUsersPages}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={usersPage === totalUsersPages}
                    onClick={() => setUsersPage(usersPage + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="suppliers">
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold text-slate-900">Supplier</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-900">Company</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-900">Membership</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-900">Status</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-900">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.filter(u => u.user_type === 'supplier').map((u) => (
                    <tr key={u.user_id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-slate-900">{u.name}</p>
                          <p className="text-sm text-slate-500">{u.email}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-700">{u.company_name || '-'}</td>
                      <td className="py-3 px-4">
                        <Badge className={
                          u.membership === 'vip' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                          u.membership === 'gold' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                          'bg-slate-50 text-slate-500 border-slate-200'
                        }>
                          {u.membership}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        {u.is_verified ? (
                          <Badge className="bg-green-50 text-green-700 border-green-200">Verified</Badge>
                        ) : (
                          <Badge className="bg-slate-50 text-slate-500 border-slate-200">Pending</Badge>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          {!u.is_verified && (
                            <Button size="sm" variant="outline" onClick={() => handleVerifyUser(u.user_id)}>
                              Verify
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => navigate(`/suppliers/${u.user_id}`)}>
                            <Eye className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="buyers">
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold text-slate-900">Buyer</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-900">Company</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-900">Country</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-900">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {users.filter(u => u.user_type === 'buyer').map((u) => (
                    <tr key={u.user_id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-slate-900">{u.name}</p>
                          <p className="text-sm text-slate-500">{u.email}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-700">{u.company_name || '-'}</td>
                      <td className="py-3 px-4 text-slate-700">{u.country || '-'}</td>
                      <td className="py-3 px-4 text-sm text-slate-500">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
